import { createContext, useContext, useEffect, useReducer, useState } from "react";
import {
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import type {
  AuthProviderAction,
  AuthProviderContextProps,
  AuthProviderProps,
  AuthProviderState,
} from "../types/AuthProvider";
import type { NavigateFunction } from "react-router-dom";

const AuthContext = createContext<AuthProviderContextProps | null>(null);
const initialState: AuthProviderState = {
  user: null,
  error: null,
};

const authReducer = (state: AuthProviderState, action: AuthProviderAction): AuthProviderState => {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.payload.user,
        error: null,
      };
    case "SET_ERROR":
      return {
        ...state,
        user: null,
        error: action.payload.error,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        error: null,
      };
    default:
      throw new Error("Reducer action type undefined");
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isLoading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    checkUser();
    const cancelHubListener = Hub.listen("auth", async ({ payload: { event } }) => {
      switch (event) {
        case "signedIn":
          await checkUser();
          break;
        case "signedOut":
          await clear();
          break;
        case "customOAuthState":
        case "signInWithRedirect":
        case "signInWithRedirect_failure":
        case "tokenRefresh":
        case "tokenRefresh_failure":
          break;
        default:
          await checkUser();
      }
    });

    return () => {
      cancelHubListener();
    };
  }, []);

  const checkUser = async (): Promise<void> => {
    try {
      const userData = await getCurrentUser();
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const attributes = await fetchUserAttributes();

      dispatch({
        type: "SET_USER",
        payload: {
          user: {
            id: attributes.sub!,
            username: userData.signInDetails?.loginId!,
            displayName: `${attributes.given_name} ${attributes.family_name}`,
            roles: idToken?.payload["cognito:groups"] as string[],
            exp: idToken?.payload.exp as number,
            idToken: idToken?.toString()!,
            profile: attributes.profile || "",
          },
          error: null,
        },
      });
    } catch (error: any) {
      console.error("Check error :", error);
      dispatch({
        type: "SET_ERROR",
        payload: {
          user: null,
          error: error.message,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmLogin = async (newPassword: string, navigator: NavigateFunction): Promise<void> => {
    try {
      setLoading(true);
      await confirmSignIn({
        challengeResponse: newPassword,
      });
      navigator("/");
    } catch (error: any) {
      console.error("Error on confirmLogin in : ", error);
      dispatch({
        type: "SET_ERROR",
        payload: {
          user: null,
          error: error.message,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const login = async (
    username: string,
    password: string,
    navigator?: NavigateFunction
  ): Promise<boolean> => {
    let result = true;
    try {
      setLoading(true);
      const res = await signIn({ username: username.toLowerCase(), password });
      if (res?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        navigator?.("/confirm-password?id=" + btoa(password) + "&username=" + username);
      } else if (res?.isSignedIn) {
        navigator?.(new URL(window.location.href).searchParams.get("redirect") || "/");
      }
    } catch (error: any) {
      console.error("Error on sign in : ", error);
      dispatch({
        type: "SET_ERROR",
        payload: {
          user: null,
          error: error.message,
        },
      });
      result = false;
    } finally {
      setLoading(false);
    }
    return result;
  };

  const logout = async (url?: string): Promise<void> => {
    try {
      await signOut();
      window.location.href = typeof url === "string" ? url : "/";
    } catch (error: any) {
      console.error("Error on sign out : ", error);
      dispatch({
        type: "SET_ERROR",
        payload: {
          user: null,
          error: error.message,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    dispatch({ type: "CLEAR_ERROR", payload: { user: null, error: null } });
  };

  const getPermissions = async (): Promise<string[]> => {
    return state.user ? Promise.resolve(state.user.roles) : Promise.resolve([]);
  };

  const value = {
    user: state.user,
    isLoading: isLoading,
    error: state.error,
    isAuthenticated: isLoading ? false : !!state.user,
    confirmLogin,
    login,
    logout,
    clear,
    getPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("use Auth must be used within AuthProvider");
  }
  return context;
};
